import type { FilterType, ShapeType } from '../../reportsTypes';

/**
 * Single source of truth for the "World shape needs a vessel selection" rule.
 *
 * A world-shape report has no geographic area, so the backend cannot derive
 * which ports/vessels to scan from a polygon. Instead it builds its target set
 * (`a_team`) from a shiplist or ship selection. A plain filter expression does
 * NOT populate `a_team`, so it is not sufficient. Without a usable selection the
 * job fails with a cryptic "No ports found for given shape" error.
 *
 * Both report-creation flows (Quick/Clone form schema and the Wizard stepper)
 * MUST use these helpers so their validation cannot drift apart.
 */
export const WORLD_SHAPE_FILTER_REQUIRED_MESSAGE =
  'World reports require a shiplist or ship filter';

/**
 * Normalized vessel selection both flows must hand to {@link
 * hasRequiredVesselSelection}. `filter` holds the primary selection (a shiplist
 * uuid string, or a ship array); `shiplist` holds the shiplist component of a
 * `combined` filter. Call sites are responsible for mapping their own storage
 * shape onto this contract so the rule stays single-sourced.
 */
export interface VesselSelection {
  filter?: unknown;
  shiplist?: unknown;
}

/** World-shape reports must restrict by a vessel selection. */
export function worldShapeRequiresVesselSelection(
  shapeType: ShapeType,
): boolean {
  return shapeType === 'worldShape';
}

/**
 * True when the selection can populate the backend `a_team`. Only a shiplist or
 * ship selection qualifies — a normal filter expression does not, including the
 * filter-expression half of a `combined` filter.
 */
export function hasRequiredVesselSelection(
  filterType: FilterType,
  values: VesselSelection,
): boolean {
  if (filterType === 'shiplist' || filterType === 'ship') {
    return isFilledFilterValue(values.filter);
  }
  if (filterType === 'combined') {
    // Only the shiplist half of a combined filter feeds `a_team`.
    return isFilledFilterValue(values.shiplist);
  }
  // 'normal' filter expressions do not populate the backend `a_team`.
  return false;
}

function isFilledFilterValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') {
    return value.trim() !== '' && value !== 'no-filter';
  }
  return false;
}
